import { AIClipProcessor } from '@src/services/AIClipProcessor';
import { clipProcessorDepsFromStore } from '@src/services/clipProcessorDepsFromStore';
import { syncVideoToStoryEntries } from '@src/services/common/syncVideoToStory';
import {
  populateExtraElementData,
  populateVideoVersionWithResources,
} from '@src/services/VideoResourcesProvider';
import { LogManager } from '@src/utility/Logger';
import { pickUnderlyingVideoElement } from '@src/utility/stackedLayout';
import { makeAutoObservable, observable } from 'mobx';

import type {
  AssociatedVideo,
  Story,
  Video,
  VideoClip,
  VideoVersion,
} from '../types.ts/story';
import type { SafeZoneVariantSnapshot } from './ClipEditSessionStore';
import type { VideoSource } from './types';
import type { CachedVideoRepository } from '@src/repositories/CachedVideoRepository';
import type { VideoRepository } from '@src/repositories/VideoRepository';
import type { SafeZoneSelection } from '@src/safe-zones/types';
import type { VideoResourceField } from '@src/services/VideoResourcesProvider';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

const logger = LogManager.getLogger('store:ClipPreviewStore');

export type CachedClipResources = {
  populatedClip: VideoClip;
  extraElementData: Video['extraElementData'] | undefined;
  associatedVideos: AssociatedVideo[];
  fetchedAt: number;
};

/**
 * Maximum number of clip resources kept in the in-memory cache.
 * Once exceeded the oldest entries (by fetchedAt) are evicted.
 */
const MAX_CACHED_CLIPS = 50;

const RESOURCE_FIELDS: VideoResourceField[] = [
  'videoSourceSnapshot',
  'transcriptionSnapshot',
  'karaokeData',
  'scenes',
];

export class ClipPreviewStore {
  cachedClipResources = new Map<string, CachedClipResources>();
  inflightClipResources = new Map<string, Promise<CachedClipResources>>();

  /** Most recent safe-zone selection across this browser session. */
  lastSafeZoneSelection: SafeZoneSelection = 'none';
  /**
   * Most recent per-overlay variant flag map. Replaced wholesale on each
   * mirror write (observable.ref) so MobX detects changes without descending
   * into the object.
   */
  lastSafeZoneVariants: SafeZoneVariantSnapshot = {};

  /**
   * PLAT-11748 — the transcript language each clip was last viewed in, keyed by
   * the parent clip id (`null` = Original). Per clip, unlike the safe zone above:
   * the language is what the user is working in, and one clip's Hungarian must
   * not open another clip in Hungarian. Read once at the next open by
   * `useClipEditSession` to seed the session (a `?clipLang=` link still wins),
   * so it is plain state, not observable.
   */
  lastClipLanguages = new Map<string, string | null>();

  constructor() {
    makeAutoObservable(this, {
      inflightClipResources: false,
      lastClipLanguages: false,
      lastSafeZoneVariants: observable.ref,
    });
  }

  /**
   * Mirror the modal's current safe-zone state into the singleton "last
   * values" so the next modal open restores it via `useClipEditSession`.
   * Single action so both fields advance atomically — preventing a transient
   * (new selection, stale variants) snapshot between two separate writes.
   */
  setLastSafeZone(
    selection: SafeZoneSelection,
    variants: SafeZoneVariantSnapshot,
  ): void {
    this.lastSafeZoneSelection = selection;
    this.lastSafeZoneVariants = variants;
  }

  /** Remember the language a clip's preview is showing (`null` = Original). */
  setLastClipLanguage(clipId: string, language: string | null): void {
    this.lastClipLanguages.set(clipId, language);
  }

  /** The language a clip was last viewed in; `null` when unknown or Original. */
  getLastClipLanguage(clipId: string | undefined | null): string | null {
    if (!clipId) return null;
    return this.lastClipLanguages.get(clipId) ?? null;
  }

  // ---------------------------------------------------------------------------
  // Static URL helpers (used by both warmer and clip preview hook)
  // ---------------------------------------------------------------------------

  /**
   * Get the low-resolution video URL from story for preview optimization.
   * Falls back to original URL if low-res not available.
   */
  static getLowResVideoUrl(story: Story | undefined): string | null {
    if (!story?.originalVideo) return null;

    const { video, url } = story.originalVideo;
    // Prefer low resolution for previews, fallback chain: low -> medium -> original
    return video?.mp4UrlLow || video?.mp4UrlMedium || url || null;
  }

  /**
   * Extract the video source URL from a VideoSource's elements.
   */
  static extractVideoUrl(videoSource: VideoSource | undefined): string | null {
    if (!videoSource?.elements?.length) return null;

    for (const element of videoSource.elements) {
      const videoElement = pickUnderlyingVideoElement(element);
      if (videoElement?.type === 'video' && videoElement.source) {
        return videoElement.source;
      }
    }
    return null;
  }

  /**
   * Pre-fetch resources for a regular clip (S3 fetch).
   * Reuses the same populateVideoVersionWithResources + populateExtraElementData
   * calls currently in useClipResourceLoader.
   *
   * Fire-and-forget from hover — deduplicates via inflight map.
   */
  prefetch(
    storyId: string,
    clip: VideoClip,
    videoRepository: VideoRepository | CachedVideoRepository,
  ): Promise<CachedClipResources> {
    const clipId = clip.id;
    if (!clipId) {
      return Promise.reject(new Error('Clip has no id'));
    }

    // Cache hit — already fetched
    const cached = this.cachedClipResources.get(clipId);
    if (cached) return Promise.resolve(cached);

    // Deduplicate in-flight requests
    const existing = this.inflightClipResources.get(clipId);
    if (existing) return existing;

    const promise = this._fetchResources(storyId, clip, videoRepository);
    this.inflightClipResources.set(clipId, promise);

    promise.finally(() => {
      this.inflightClipResources.delete(clipId);
    });

    return promise;
  }

  /**
   * Pre-expand a lightweight clip (generates videoSource from parent transcript).
   * Wraps AIClipProcessor.expandLightweightClip and maps its output into the
   * CachedClipResources shape.
   *
   * Also patches story.otherVideos in memory so the clip is permanently upgraded.
   */
  prefetchLightweight(
    clip: VideoClip,
    story: Story,
    videoCreator: IVideoCreatorStore,
  ): Promise<CachedClipResources> {
    const clipId = clip.id;
    if (!clipId) {
      return Promise.reject(new Error('Clip has no id'));
    }

    // Cache hit — already expanded
    const cached = this.cachedClipResources.get(clipId);
    if (cached) return Promise.resolve(cached);

    // Deduplicate in-flight requests (shared with prefetch)
    const existing = this.inflightClipResources.get(clipId);
    if (existing) return existing;

    const promise = this._expandLightweight(clip, story, videoCreator);
    this.inflightClipResources.set(clipId, promise);

    promise.finally(() => {
      this.inflightClipResources.delete(clipId);
    });

    return promise;
  }

  /** Get cached result, or undefined. */
  getClipResource(clipId: string): CachedClipResources | undefined {
    return this.cachedClipResources.get(clipId);
  }

  /**
   * Enrich a clip with pre-fetched resources from the cache.
   * Returns the merged clip, or undefined if nothing is cached for this ID.
   */
  enrichClipFromCache(clip: VideoClip, clipId: string): VideoClip | undefined {
    const cached = this.cachedClipResources.get(clipId);
    if (!cached) return undefined;
    return {
      ...clip,
      ...cached.populatedClip,
      extraElementData: cached.extraElementData ?? clip.extraElementData,
      associatedVideos: cached.associatedVideos.length
        ? cached.associatedVideos
        : clip.associatedVideos,
    };
  }

  /**
   * Invalidate cache entries so the next open re-fetches fresh data (e.g.
   * after a save or rename). Pass the clip id together with its parent id:
   * the parent entry holds a stale `associatedVideos` snapshot when the clip
   * is a non-main variant, so invalidating the clip id alone would leave that
   * variant stale. Undefined ids and duplicates are ignored.
   */
  invalidate(...clipIds: (string | undefined)[]): void {
    for (const clipId of clipIds) {
      if (clipId) this.cachedClipResources.delete(clipId);
    }
  }

  /** Clear all cached clip resources (e.g. when leaving Content Studio). */
  clearClipResourceCache(): void {
    this.cachedClipResources.clear();
    this.inflightClipResources.clear();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private async _fetchResources(
    storyId: string,
    clip: VideoClip,
    videoRepository: VideoRepository | CachedVideoRepository,
  ): Promise<CachedClipResources> {
    try {
      // Load main clip resources, extraElementData, and associated videos all in parallel
      const associatedVideoPromises = (clip.associatedVideos ?? []).map(
        async associatedVideo => {
          if (!associatedVideo.id) return associatedVideo;
          const [populated, item] = await Promise.all([
            populateVideoVersionWithResources(
              storyId,
              { ...associatedVideo },
              [...RESOURCE_FIELDS],
              false,
              'latest',
              false,
            ),
            videoRepository
              ? populateExtraElementData(videoRepository, associatedVideo.id)
              : Promise.resolve(undefined),
          ]);
          return {
            ...populated,
            extraElementData:
              item?.extraElementData ?? populated.extraElementData,
            versionId: item?.versionId,
            resourcesMetadata: item?.resourcesMetadata,
          };
        },
      );

      const [populatedClip, item, ...associatedVideos] = await Promise.all([
        populateVideoVersionWithResources(
          storyId,
          { ...clip },
          [...RESOURCE_FIELDS],
          false,
          'latest',
          false,
        ),
        clip.id && videoRepository
          ? populateExtraElementData(videoRepository, clip.id)
          : Promise.resolve(undefined),
        ...associatedVideoPromises,
      ]);

      // A regular (non-temp) preview clip must resolve a videoSource — without
      // it the renderer can't initialize. Treat a missing videoSource as a hard
      // failure (e.g. a rejected resource request) and throw rather than caching
      // a resourceless result, which would poison this clip's cache entry and
      // hang every subsequent open until the cache is cleared.
      if (!populatedClip.videoSource) {
        throw new Error(
          `Clip ${clip.id} has no videoSource after resource fetch (story ${storyId})`,
        );
      }

      const result: CachedClipResources = {
        populatedClip: {
          ...populatedClip,
          versionId: item?.versionId,
          resourcesMetadata: item?.resourcesMetadata,
        },
        extraElementData: item?.extraElementData,
        associatedVideos: associatedVideos,
        fetchedAt: Date.now(),
      };

      this.cachedClipResources.set(clip.id!, result);
      this._evictIfNeeded();

      logger.info('Clip resources pre-fetched', {
        clipId: clip.id,
        associatedCount: associatedVideos.length,
      });

      return result;
    } catch (error) {
      logger.error('Failed to pre-fetch clip resources', {
        clipId: clip.id,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  private async _expandLightweight(
    clip: VideoClip,
    story: Story,
    videoCreator: IVideoCreatorStore,
  ): Promise<CachedClipResources> {
    try {
      // Create a working copy to expand
      const expandedVideo = { ...clip } as VideoVersion;

      await AIClipProcessor.expandLightweightClip(
        expandedVideo,
        story,
        clipProcessorDepsFromStore(videoCreator),
      );

      // Verify expansion succeeded
      if (!expandedVideo.videoSource) {
        throw new Error('Expansion completed but videoSource is still empty');
      }

      // Patch story.otherVideos so the clip is permanently upgraded
      if (story.otherVideos) {
        syncVideoToStoryEntries(story.otherVideos, clip.id, expandedVideo);
      }

      const result: CachedClipResources = {
        populatedClip: {
          ...clip,
          videoSource: expandedVideo.videoSource,
          transcriptionSnapshot: expandedVideo.transcriptionSnapshot,
          karaokeData: expandedVideo.karaokeData,
          scenes: expandedVideo.scenes,
          // The expansion amends `clipJson` — it is where a reel's Section
          // names are seeded from its sub-clips — so the cache holds the
          // amended one rather than the copy `...clip` brought in.
          clipJson: expandedVideo.clipJson,
        } as VideoClip,
        extraElementData: undefined,
        associatedVideos: [],
        fetchedAt: Date.now(),
      };

      this.cachedClipResources.set(clip.id!, result);
      this._evictIfNeeded();

      logger.info('Lightweight clip expanded and cached', {
        clipId: clip.id,
      });

      return result;
    } catch (error) {
      logger.error('Failed to expand lightweight clip', {
        clipId: clip.id,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /** Evict the oldest entries when the cache exceeds MAX_CACHED_CLIPS. */
  private _evictIfNeeded(): void {
    if (this.cachedClipResources.size <= MAX_CACHED_CLIPS) return;

    const entries = [...this.cachedClipResources.entries()].sort(
      (a, b) => a[1].fetchedAt - b[1].fetchedAt,
    );

    const toRemove = entries.length - MAX_CACHED_CLIPS;
    for (let i = 0; i < toRemove; i++) {
      this.cachedClipResources.delete(entries[i][0]);
    }

    logger.info('Evicted oldest clip resource entries', { evicted: toRemove });
  }
}
